import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Badge, Spinner } from "react-bootstrap";
import defaultTemplateImage from "../../assets/images/template-default.png";
import TemplateService from "../../services/TemplateService";

// ─── Template Preview Card ───────────────────────────────────────────────────
const CARD_WIDTH = 220;
const CARD_HEIGHT = 150;

const TemplateCard = ({ template, onSelect }) => {
  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;

  const backgroundImage = template.templateImage
    ? `${BASE_URL}${template.templateImage}`
    : defaultTemplateImage;

  const templateBodyRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [imageSize, setImageSize] = useState({ width: 900, height: 400 });

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = backgroundImage;
    img.onload = () => setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => setImageSize({ width: 900, height: 400 });
  }, [backgroundImage]);

  useEffect(() => {
    if (imageSize.width === 0 || imageSize.height === 0) return;
    const scaleX = CARD_WIDTH / imageSize.width;
    const scaleY = CARD_HEIGHT / imageSize.height;
    setScale(Math.min(scaleX, scaleY));
  }, [imageSize]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(template)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.(template);
        }
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        width: `${CARD_WIDTH}px`,
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Preview card */}
      <div
        style={{
          width: `${CARD_WIDTH}px`,
          height: `${CARD_HEIGHT}px`,
          backgroundColor: "#fff",
          overflow: "hidden",
          position: "relative",
          borderRadius: "6px 6px 0 0",
          border: "1px solid #d1d5db",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: `${imageSize.width}px`,
            height: `${imageSize.height}px`,
            position: "absolute",
            top: 0,
            left: 0,
            overflow: "hidden",
          }}
        >
          <div
            ref={templateBodyRef}
            dangerouslySetInnerHTML={{ __html: template.templateBody }}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              width: "100%",
              height: "100%",
              position: "relative",
              padding: 0,
              margin: 0,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          />
        </div>
      </div>

      {/* Name bar */}
      <div
        style={{
          width: "100%",
          border: "1px solid #d1d5db",
          borderTop: "none",
          borderRadius: "0 0 6px 6px",
          textAlign: "center",
          padding: "6px 4px",
          backgroundColor: "#f9fafb",
          fontSize: "12px",
          fontWeight: "500",
          color: "#374151",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {template.name}
      </div>
    </div>
  );
};

// ─── Super Admin Cards Section grouped by category ───────────────────────────
const SuperAdminTemplateCards = ({ templates, onSelectSample }) => {
  const grouped = templates.reduce((acc, t) => {
    acc[t.templateCategory] = acc[t.templateCategory] || [];
    acc[t.templateCategory].push(t);
    return acc;
  }, {});

  return (
    <>
      {Object.keys(grouped).map((category) => (
        <div key={category} style={{ marginBottom: "24px" }}>
          {/* Category label */}
          <h6
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: "#6b7280",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "12px",
            }}
          >
            {category}
          </h6>

          {/* Cards wrap naturally — no wide empty columns */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            {grouped[category].map((tpl) => (
              <TemplateCard key={tpl.id} template={tpl} onSelect={onSelectSample} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

// ─── Main TemplateListing ────────────────────────────────────────────────────
const TemplateListing = ({ onCreateNew, onSelectSample }) => {
  const [adminTemplates, setAdminTemplates] = useState([]);
  const [superAdminTemplates, setSuperAdminTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllTemplates();
  }, []);

  const fetchAllTemplates = async () => {
    setLoading(true);

    try {
      const adminRes = await TemplateService.fetchTemplates();

      if (adminRes.data?.success && Array.isArray(adminRes.data.data)) {
        setAdminTemplates(
          adminRes.data.data.map((t) => ({
            id: t.id,
            name: t.name,
            templateBody: t.templateBody,
            templateDetails: t.templateDetails,
            templateCategory: t.templateCategory,
            active: true,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching admin templates:", error);
    }

    try {
      const superAdminRes = await TemplateService.fetchSuperAdminTemplates();

      if (superAdminRes.data?.success && Array.isArray(superAdminRes.data.data)) {
        setSuperAdminTemplates(
          superAdminRes.data.data.map((t) => ({
            id: t.id,
            name: t.name,
            templateBody: t.templateBody,
            templateDetails: t.templateDetails,
            templateCategory: t.templateCategory,
            templateImage: t.templateImage,
          }))
        );
      } else {
        setSuperAdminTemplates([]);
      }
    } catch (error) {
      console.error("Error fetching sample templates:", error);
      setSuperAdminTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSwitch = (id) => {
    setAdminTemplates((prev) =>
      prev.map((tpl) => (tpl.id === id ? { ...tpl, active: !tpl.active } : tpl))
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Templates</h2>
        <Button variant="primary" onClick={onCreateNew}>
          Create New Template
        </Button>
      </div>

      {/* ── My Templates — table ── */}
      <div className="mb-2 d-flex align-items-center gap-2">
        <h5 className="mb-0">My Templates</h5>
        <Badge bg="secondary" pill>
          {adminTemplates.length}
        </Badge>
      </div>

      <table className="table table-bordered mt-3">
        <thead className="table-light">
          <tr>
            <th>Name</th>
            <th>Category</th>
            <th>Active</th>
          </tr>
        </thead>
        <tbody>
          {adminTemplates.length === 0 ? (
            <tr>
              <td colSpan={3} className="text-center text-muted">
                No templates yet. Create your first one!
              </td>
            </tr>
          ) : (
            adminTemplates.map((tpl) => (
              <tr key={`admin-${tpl.id}`}>
                <td>{tpl.name}</td>
                <td>{tpl.templateCategory}</td>
                <td className="text-center">
                  <Form.Check
                    type="switch"
                    id={`switch-${tpl.id}`}
                    checked={tpl.active}
                    onChange={() => toggleSwitch(tpl.id)}
                    label=""
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {superAdminTemplates.length > 0 && (
        <>
          <hr style={{ margin: "32px 0", borderTop: "2px solid #e5e7eb" }} />

          <div className="mb-3 d-flex align-items-center gap-2">
            <h5 className="mb-0">Sample Available Templates</h5>
            <Badge bg="primary" pill>
              {superAdminTemplates.length}
            </Badge>
          </div>

          <SuperAdminTemplateCards
            templates={superAdminTemplates}
            onSelectSample={onSelectSample}
          />
        </>
      )}
    </div>
  );
};

export default TemplateListing;